import { IconBan, IconClockExclamation, IconShieldX, type IconProps } from "@tabler/icons-react";
import type { ComponentType } from "react";
import { ThemeIcon } from "../ThemeIcon";

/**
 * Reemplazo del Stepper cuando la solicitud cayó en un estado terminal
 * negativo (`DadaDeBajaPorAutorizador`/`CanceladaPorSolicitante`/
 * `DescartadaPorPlazoVencido`). Composición horizontal: ícono redondo +
 * título + descripción + (opcional) motivos — mismo copy y estructura
 * que `stepper/EstadoTerminalAlert.tsx` (Mantine), reconstruido con
 * `color-mix()` + tokens Tailwind en vez de `var(--mantine-color-red-*)`.
 */
interface EstadoTerminalInfo {
  icon: ComponentType<IconProps>;
  titulo: string;
  descripcion: string;
}

const ESTADO_TERMINAL_INFO: Record<string, EstadoTerminalInfo> = {
  CanceladaPorSolicitante: {
    icon: IconBan,
    titulo: "Solicitud cancelada",
    descripcion: "El solicitante decidió no continuar con esta solicitud. El flujo se cerró y no avanzará a autorización.",
  },
  DadaDeBajaPorAutorizador: {
    icon: IconShieldX,
    titulo: "Baja por Mesa de Control",
    descripcion: "Mesa de Control dio de baja esta solicitud durante la revisión. Si necesitas reintentarla, debes crear una nueva.",
  },
  DescartadaPorPlazoVencido: {
    icon: IconClockExclamation,
    titulo: "Plazo vencido",
    descripcion: "Pasaron 15 días sin completar el flujo. La solicitud se cerró automáticamente. Crea una nueva si aún necesitas el pago.",
  },
};

const FALLBACK: EstadoTerminalInfo = {
  icon: IconBan,
  titulo: "Solicitud cerrada",
  descripcion: "Esta solicitud llegó a un estado terminal y el flujo no continúa.",
};

export function EstadoTerminalAlert({
  estado,
  motivos,
  dadaDeBajaPorNombre,
}: {
  estado: string;
  motivos?: string[];
  dadaDeBajaPorNombre?: string | null;
}) {
  const info = ESTADO_TERMINAL_INFO[estado] ?? FALLBACK;
  const Icon = info.icon;
  const tieneMotivos = (motivos?.length ?? 0) > 0;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="rounded-md border-l-[3px] p-4"
      style={{
        backgroundColor: "color-mix(in srgb, var(--color-peligro-600) 6%, var(--background))",
        borderColor: "color-mix(in srgb, var(--color-peligro-600) 18%, transparent)",
        borderLeftColor: "var(--color-peligro-600)",
      }}
    >
      <div className="flex items-start gap-3">
        <ThemeIcon size="lg" radius="xl" variant="light" color="peligro">
          <Icon size={20} stroke={1.7} aria-hidden />
        </ThemeIcon>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-peligro-800 dark:text-peligro-200">{info.titulo}</p>
          <p className="text-sm leading-relaxed text-peligro-700 dark:text-peligro-300">{info.descripcion}</p>

          {estado === "DadaDeBajaPorAutorizador" && dadaDeBajaPorNombre && (
            <p className="text-sm leading-relaxed text-peligro-700 dark:text-peligro-300">
              Dada de baja por <span className="font-semibold">{dadaDeBajaPorNombre}</span> (Mesa de Control).
            </p>
          )}

          {tieneMotivos && (
            <div className="mt-1.5 space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-wide text-peligro-800 dark:text-peligro-200">
                {motivos!.length === 1 ? "Motivo" : "Motivos"}
              </p>
              {motivos!.map((m, i) => (
                <p key={i} className="text-sm leading-snug text-peligro-700 dark:text-peligro-300">
                  · {m}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
